import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  HelpCircle, X, Send, Loader2, ArrowRight, Sparkles, AlertTriangle, ChevronRight,
} from "lucide-react";
import { api } from "../lib/api";
import useEscClose from "../hooks/useEscClose";

/**
 * HelpAssistant — floating "Ask the app" bubble + slide-in chat panel.
 *
 * Coaches ask natural-language questions ("where do I enter parent
 * heights?") and Claude answers using the app-map system prompt on the
 * server. Answers include an optional "Take me there" CTA that the
 * caller-supplied `onNavigate` handler resolves — App.js wires this
 * into tab-switching + user-menu triggers.
 *
 * Props:
 *   currentScreen   — e.g. "Data Editor"  (context sent to the LLM)
 *   currentSailorId — active athlete, if any
 *   onNavigate({route, params}) — called when the coach clicks the CTA
 */
export default function HelpAssistant({ currentScreen, currentSailorId, currentSailorName, onNavigate }) {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]); // [{id, role, text, cta?, confidence?, followups?}]
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  // Monotonic message id — stable React key that survives re-orders/prepends
  // (which we don't do today, but the review flagged index-as-key). Ref so
  // we don't retrigger renders when it ticks.
  const nextIdRef = useRef(1);
  const makeId = () => nextIdRef.current++;
  useEscClose(open, () => setOpen(false));

  // Suggest a starter prompt when the panel first opens on a given
  // screen so coaches have a natural jumping-off point.
  const initialFollowups = useMemo(() => ([
    "Where do I enter parent heights?",
    "How do I add a lab result?",
    "How do I run a bulk deworming?",
    "How do I connect an athlete's WHOOP?",
  ]), []);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const ask = async (q) => {
    const text = (q ?? question).trim();
    if (!text) return;
    setMessages((m) => [...m, { id: makeId(), role: "user", text }]);
    setQuestion("");
    setBusy(true);
    try {
      const r = await api.post("/help/ask", {
        question: text,
        context: {
          current_screen: currentScreen,
          current_sailor_id: currentSailorId,
          current_sailor_name: currentSailorName,
        },
      });
      const d = r.data || {};
      setMessages((m) => [...m, {
        id: makeId(),
        role: "assistant",
        text: d.answer || "(no answer returned)",
        cta: d.cta || null,
        confidence: d.confidence || "medium",
        followups: d.followups || [],
      }]);
    } catch (e) {
      setMessages((m) => [...m, {
        id: makeId(),
        role: "assistant",
        text: e?.response?.data?.detail || e?.message || "Couldn't reach the help assistant.",
        confidence: "low",
      }]);
    } finally {
      setBusy(false);
    }
  };

  const handleCta = (cta) => {
    if (!cta) return;
    onNavigate?.(cta);
    // Leave the panel open so the coach can follow up
  };

  return (
    <>
      {/* Floating help button — bottom-right. The "Made with Emergent"
          badge that used to overlap this corner has been removed from
          public/index.html, so this is once again the natural home. */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-4 right-4 z-[190] rounded-full pl-3 pr-4 py-2 shadow-2xl border border-cyan-400/60 bg-[#0B1627] text-cyan-200 hover:bg-cyan-500/15 transition-colors flex items-center gap-1.5"
          data-testid="help-bubble"
          title="Ask the app anything"
        >
          <HelpCircle size={18} />
          <span className="text-[11.5px] font-display tracking-wide">Ask</span>
        </button>
      )}

      {open && createPortal(
        <div
          className="fixed inset-0 z-[195] bg-[#040D1A]/40 flex items-end sm:items-center justify-end pointer-events-none"
          data-testid="help-panel-root"
        >
          <div
            className="pointer-events-auto card w-full sm:w-[420px] max-h-[90vh] flex flex-col shadow-2xl border border-cyan-400/40 mr-0 sm:mr-4 mb-0 sm:mb-4"
            data-testid="help-panel"
          >
            <div className="flex items-start justify-between p-4 border-b border-[#1E304B]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-cyan-300 flex items-center gap-1.5">
                  <Sparkles size={11} /> Ask the app
                </div>
                <div className="font-display text-base text-slate-100 mt-0.5">Powered by Claude</div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Ask anything about the dashboard — where a field lives, how to do a task, what a term means.
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="pill !p-1.5"
                      data-testid="help-close"><X size={14} /></button>
            </div>

            <div ref={scrollRef}
                 className="flex-1 overflow-y-auto p-4 space-y-3 text-[13px]"
                 data-testid="help-conversation">
              {messages.length === 0 && (
                <div className="text-slate-400">
                  <div className="mb-2">Hi! I can help you find any feature in the dashboard.</div>
                  <div className="space-y-1">
                    {initialFollowups.map((q) => (
                      <SuggestionChip key={q} label={q} onClick={() => ask(q)} />
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m) => (
                <MessageRow key={m.id} msg={m} onCta={handleCta} onFollowup={ask} />
              ))}
              {busy && (
                <div className="text-slate-500 text-[12px] flex items-center gap-2" data-testid="help-loading">
                  <Loader2 size={12} className="animate-spin" /> Thinking…
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); ask(); }}
              className="p-3 border-t border-[#1E304B] flex items-end gap-2"
              data-testid="help-form"
            >
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    ask();
                  }
                }}
                rows={1}
                placeholder="Ask a question…"
                className="flex-1 resize-none rounded-lg bg-[#0B1627] border border-[#1E304B] px-3 py-2 text-[13px] placeholder-slate-500 focus:outline-none focus:border-cyan-400/60 min-h-[38px] max-h-[100px]"
                data-testid="help-input"
                disabled={busy}
              />
              <button
                type="submit"
                disabled={busy || !question.trim()}
                className="pill active !p-2 disabled:opacity-40"
                data-testid="help-send"
                title="Send (Enter)"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </form>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

function MessageRow({ msg, onCta, onFollowup }) {
  if (msg.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="rounded-lg bg-cyan-500/15 border border-cyan-400/30 px-3 py-1.5 max-w-[80%] text-slate-100 whitespace-pre-wrap"
             data-testid="help-msg-user">
          {msg.text}
        </div>
      </div>
    );
  }
  const conf = msg.confidence || "medium";
  return (
    <div className="flex flex-col gap-2" data-testid="help-msg-assistant">
      <div className="rounded-lg bg-[#0B1627] border border-[#1E304B] px-3 py-2 text-slate-200 whitespace-pre-wrap">
        {/* Render **bold** as <strong> for readability of tab/button names */}
        <RenderBold text={msg.text} />
        {conf === "low" && (
          <div className="mt-1.5 text-[10px] text-amber-300/80 flex items-center gap-1"
               data-testid="help-low-confidence">
            <AlertTriangle size={10} /> low confidence
          </div>
        )}
      </div>
      {msg.cta && (
        <button
          onClick={() => onCta(msg.cta)}
          className="self-start pill active !text-[11px] !py-1 !px-2.5"
          data-testid="help-cta"
        >
          <ArrowRight size={11} /> {msg.cta.label || "Take me there"}
        </button>
      )}
      {msg.followups && msg.followups.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {msg.followups.map((f) => (
            <SuggestionChip key={f} label={f} onClick={() => onFollowup(f)} compact />
          ))}
        </div>
      )}
    </div>
  );
}

function SuggestionChip({ label, onClick, compact }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full border border-[#1E304B] text-slate-300 hover:text-cyan-200 hover:border-cyan-400/50 transition-colors ${compact ? "text-[10.5px] px-2 py-0.5" : "text-[11px] px-2.5 py-1"}`}
      data-testid="help-suggestion"
    >
      <ChevronRight size={9} className="opacity-60" />
      {label}
    </button>
  );
}

function RenderBold({ text }) {
  // Simple markdown-lite: render **bold** and ignore everything else.
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((p, i) => {
        // Index + a slice of the content keeps the key stable across the
        // typical text-only re-renders while still being unique between
        // adjacent parts of the same message.
        const key = `${i}-${p.slice(0, 12)}`;
        return p.startsWith("**") && p.endsWith("**")
          ? <strong key={key} className="text-cyan-200">{p.slice(2, -2)}</strong>
          : <React.Fragment key={key}>{p}</React.Fragment>;
      })}
    </>
  );
}
